import {
	ApolloClient,
	ApolloLink,
	InMemoryCache,
	split,
} from '@apollo/client';

import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient as createGraphQLWsClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';

import { broadcastTenantSwitch } from '../utils/tenantSwitchSync';
import {
	createSubscriptionConnection,
	createSubscriptionRetryController,
} from './subscriptionConnection';

// The server rejects requests whose token is invalid or expired with this exact
// error from the request context, carrying the stable AUTH_SESSION_EXPIRED code.
// Auth tokens now carry an expiry, so an expired session must drop the stored
// token and return to the login screen instead of leaving the user on a
// permanently loading screen.
const AUTH_CONTEXT_ERROR_MESSAGE = 'Error: authenticate - user not found';
const AUTH_CONTEXT_ERROR_CODE = 'AUTH_SESSION_EXPIRED';

// Apollo prefixes plain errors from the request context ("Context creation
// failed: …"), so the stable code is the primary signal; the message check is a
// fallback for any path that still throws the plain message.
// A query's error keeps the server's errors in graphQLErrors, or in networkError.result.errors
// when they came with an HTTP error status (the request context fails with status 500).
export const isAuthContextError = error => [
	...(error?.graphQLErrors || []),
	...(error?.networkError?.result?.errors || []),
].some(graphqlError => (
	graphqlError?.extensions?.code === AUTH_CONTEXT_ERROR_CODE ||
	graphqlError?.message?.includes(AUTH_CONTEXT_ERROR_MESSAGE)
));

const createAuthExpiredLink = onAuthExpired => onError((error) => {
	if (!isAuthContextError(error)) {
		return;
	}

	if (typeof onAuthExpired === 'function') {
		onAuthExpired();
		return;
	}

	// Default browser behavior: drop the stored token and return to the login
	// screen. React Native apps pass no handler and have no window.location, so
	// they keep their own session handling and must not hit browser APIs.
	if (typeof window === 'undefined' || typeof window.location?.assign !== 'function') {
		return;
	}

	try {
		localStorage.removeItem('token');
	}
	catch (storageError) {
		// Storage may be unavailable (private mode); the redirect still happens.
	}

	window.location.assign('/login');
});

export default async function createClient (args) {
	const {
		getToken,
		getApiHost,
		getwsHost,
		fullorigin,
		refetchOnSubscriptionReconnect = false,
		onAuthExpired,
		// Sent with every request, for a client the server needs to tell apart.
		headers: clientHeaders = {},
	} = args;

	const httpLink = createUploadLink({ uri: await getApiHost() });

	const authLink = setContext(async (_, { headers }) => {
		const token = await getToken();

		return {
			headers: {
				...headers,
				...clientHeaders,
				authorization: token ? `JWT ${token}` : '',
				...(fullorigin ? { fullorigin } : {}),
			},
		};
	});

	const wsUrl = await getwsHost();
	const subscriptionConnection = createSubscriptionConnection();
	const subscriptionRetryController = createSubscriptionRetryController();
	let client = null;

	const wsClient = createGraphQLWsClient({
		url: wsUrl,
		// Only keep the socket open while there are active subscriptions.
		lazy: true,
		lazyCloseTimeout: 0,
		connectionParams: async () => {
			const token = await getToken();
			return {
				authToken: token,
			};
		},
		// Keep trying through prolonged outages, but cap each wait so recovery
		// never stalls indefinitely after the app becomes reachable again.
		retryAttempts: Infinity,
		retryWait: subscriptionRetryController.wait,
		shouldRetry: () => true,
		on: {
			connecting: subscriptionConnection.connecting,
			connected: (...connectionArgs) => {
				const [, , wasRetry] = connectionArgs;
				const didRecover = subscriptionConnection.connected(wasRetry);
				if (didRecover && refetchOnSubscriptionReconnect) {
					client?.refetchQueries({ include: 'active' }).catch(() => {});
				}
			},
			closed: subscriptionConnection.disconnected,
			error: subscriptionConnection.disconnected,
		},
	});
	subscriptionConnection.setReconnect(() => {
		subscriptionRetryController.retryNow();
		wsClient.terminate();
	});

	const wsLink = new GraphQLWsLink(wsClient);

	const isSwitchTenantOperation = (operation) => {
		if (operation?.operationName === 'switchTenant') {
			return true;
		}

		const definition = getMainDefinition(operation.query);
		if (definition?.kind !== 'OperationDefinition' || definition.operation !== 'mutation') {
			return false;
		}

		return definition.selectionSet?.selections?.some(selection => (
			selection?.kind === 'Field' && selection?.name?.value === 'switchTenant'
		));
	};

	const tenantSwitchBroadcastLink = new ApolloLink((operation, forward) => {
		const shouldBroadcast = isSwitchTenantOperation(operation);

		return forward(operation).map((result) => {
			// Only broadcast once the server confirms the switch succeeded.
			if (shouldBroadcast && !result?.errors?.length) {
				broadcastTenantSwitch(operation?.variables?.tenantId);
			}

			return result;
		});
	});

	// using the ability to split links, you can send data to each link
	// depending on what kind of operation is being sent
	const splitLink = split(
		// split based on operation type
		({ query }) => {
			const definition = getMainDefinition(query);
			return (
				definition.kind === 'OperationDefinition' &&
				definition.operation === 'subscription'
			);
		},
		wsLink,
		createAuthExpiredLink(onAuthExpired).concat(authLink).concat(tenantSwitchBroadcastLink).concat(httpLink),
	);

	const cache = new InMemoryCache();

	client = new ApolloClient({
		link: splitLink,
		cache,
		defaultOptions: {
			watchQuery: {
				fetchPolicy: 'cache-and-network',
				nextFetchPolicy: 'cache-first',
			},
		},
	});
	client.subscriptionConnection = subscriptionConnection;

	return client;
}
